"use client";

import React, { useState } from "react";
import { useToast } from "./Toast";

interface AdsCampaignGeneratorProps {
  className?: string;
}

export default function AdsCampaignGenerator({ className = "" }: AdsCampaignGeneratorProps) {
  const [product, setProduct] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [platform, setPlatform] = useState("FACEBOOK");
  const [budget, setBudget] = useState("");
  const [campaignGoal, setCampaignGoal] = useState("CONVERSÕES");
  const [adFormat, setAdFormat] = useState("MÚLTIPLOS");
  const [duration, setDuration] = useState("30 DIAS");
  const [campaign, setCampaign] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToast();

  const generateCampaign = async () => {
    if (!product || !targetAudience || !platform || !budget) {
      addToast("⚠️ Preencha todos os campos obrigatórios", "error");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch("/api/ads-campaign", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          product, 
          targetAudience, 
          platform, 
          budget, 
          campaignGoal, 
          adFormat, 
          duration 
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCampaign(data);
        addToast("📱 Campanha de ads gerada com sucesso!", "success");
      } else {
        const error = await response.json();
        addToast(error.error || "Erro ao gerar campanha", "error");
      }
    } catch (error) {
      console.error("Erro:", error);
      addToast("Erro ao gerar campanha", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const copyCampaignData = (data: string) => {
    navigator.clipboard.writeText(data);
    addToast("📋 Dados da campanha copiados!", "success");
  };

  return (
    <div className={`bg-gradient-to-br from-pink-900 via-purple-900 to-indigo-900 text-white rounded-lg p-6 border border-pink-500/30 font-mono ${className}`}>
      <div className="mb-6">
        <h3 className="text-2xl font-bold mb-2 text-pink-300">
          📱 ADS CAMPAIGN GENERATOR 📱
        </h3>
        <div className="text-xs text-pink-400 mb-4">
          [ADS_AUTOMATION] v2.0.0 | STATUS: ACTIVE | AUTH: ADS_MASTER
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-xs text-pink-400 mb-2">PRODUCT_NAME:</label>
          <input
            type="text"
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            className="w-full bg-pink-950/50 border border-pink-500/50 text-pink-300 px-3 py-2 rounded focus:outline-none focus:border-pink-400 focus:shadow-[0_0_10px_rgba(236,72,153,0.3)]"
            placeholder="ex: Curso de Marketing Digital"
          />
        </div>

        <div>
          <label className="block text-xs text-pink-400 mb-2">TARGET_AUDIENCE:</label>
          <input
            type="text"
            value={targetAudience}
            onChange={(e) => setTargetAudience(e.target.value)}
            className="w-full bg-pink-950/50 border border-pink-500/50 text-pink-300 px-3 py-2 rounded focus:outline-none focus:border-pink-400"
            placeholder="ex: Empreendedores iniciantes"
          />
        </div>

        <div>
          <label className="block text-xs text-pink-400 mb-2">PLATFORM:</label>
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="w-full bg-pink-950/50 border border-pink-500/50 text-pink-300 px-3 py-2 rounded focus:outline-none focus:border-pink-400"
          >
            <option value="FACEBOOK">FACEBOOK_ADS</option>
            <option value="INSTAGRAM">INSTAGRAM_ADS</option>
            <option value="GOOGLE">GOOGLE_ADS</option>
            <option value="TIKTOK">TIKTOK_ADS</option>
            <option value="LINKEDIN">LINKEDIN_ADS</option>
            <option value="MÚLTIPLAS">MULTI_PLATFORM</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-pink-400 mb-2">BUDGET:</label>
          <input
            type="text"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            className="w-full bg-pink-950/50 border border-pink-500/50 text-pink-300 px-3 py-2 rounded focus:outline-none focus:border-pink-400"
            placeholder="ex: 5000.00"
          />
        </div>

        <div>
          <label className="block text-xs text-pink-400 mb-2">CAMPAIGN_GOAL:</label>
          <select
            value={campaignGoal}
            onChange={(e) => setCampaignGoal(e.target.value)}
            className="w-full bg-pink-950/50 border border-pink-500/50 text-pink-300 px-3 py-2 rounded focus:outline-none focus:border-pink-400"
          >
            <option value="CONVERSÕES">CONVERSIONS</option>
            <option value="TRÁFEGO">TRAFFIC</option>
            <option value="LEADS">LEAD_GENERATION</option>
            <option value="VENDAS">SALES</option>
            <option value="MARCA">BRAND_AWARENESS</option>
            <option value="ENGAJAMENTO">ENGAGEMENT</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-pink-400 mb-2">AD_FORMAT:</label>
          <select
            value={adFormat}
            onChange={(e) => setAdFormat(e.target.value)}
            className="w-full bg-pink-950/50 border border-pink-500/50 text-pink-300 px-3 py-2 rounded focus:outline-none focus:border-pink-400"
          >
            <option value="MÚLTIPLOS">MULTI_FORMAT</option>
            <option value="IMAGEM">IMAGE_ONLY</option>
            <option value="VÍDEO">VIDEO_ONLY</option>
            <option value="CARROSSEL">CAROUSEL</option>
            <option value="STORIES">STORIES_ONLY</option>
            <option value="REELS">REELS_ONLY</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-pink-400 mb-2">DURATION:</label>
          <select
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            className="w-full bg-pink-950/50 border border-pink-500/50 text-pink-300 px-3 py-2 rounded focus:outline-none focus:border-pink-400"
          >
            <option value="7 DIAS">7_DAYS</option>
            <option value="14 DIAS">14_DAYS</option>
            <option value="30 DIAS">30_DAYS</option>
            <option value="60 DIAS">60_DAYS</option>
            <option value="90 DIAS">90_DAYS</option>
          </select>
        </div>
      </div>

      <button
        onClick={generateCampaign}
        disabled={isLoading || !product || !targetAudience || !platform || !budget}
        className="w-full bg-gradient-to-r from-pink-600 to-purple-600 text-white py-3 px-6 rounded font-mono text-sm hover:from-pink-700 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-600 transition-all duration-300 mb-6"
      >
        {isLoading ? (
          <span className="flex items-center justify-center">
            <span className="animate-pulse">⚡</span> BUILDING_CAMPAIGN...
          </span>
        ) : (
          <span className="flex items-center justify-center">
            📱 [GENERATE_CAMPAIGN] 📱
          </span>
        )}
      </button>

      {campaign && (
        <div className="mt-6 space-y-4">
          <div className="text-xs text-pink-400 mb-4">
            [CAMPAIGN_GENERATED] | NAME: {campaign.campaign_name} | PLATFORM: {campaign.platform} | BUDGET: R$ {campaign.budget.total}
          </div>
          
          {/* Campaign Overview */}
          <div className="bg-pink-950/30 border border-pink-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-pink-300 mb-3">📊 CAMPAIGN_OVERVIEW</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div className="bg-pink-900/30 p-2 rounded">
                <span className="text-pink-400">OBJECTIVE:</span>
                <div className="text-pink-300 font-bold">{campaign.campaign_objective}</div>
              </div>
              <div className="bg-pink-900/30 p-2 rounded">
                <span className="text-pink-400">DURATION:</span>
                <div className="text-pink-300 font-bold">{campaign.duration}</div>
              </div>
              <div className="bg-pink-900/30 p-2 rounded">
                <span className="text-pink-400">ESTIMATED_ROAS:</span>
                <div className="text-pink-300 font-bold">{campaign.estimated_performance?.expected_roas}</div>
              </div>
            </div>
          </div>

          {/* Targeting */}
          <div className="bg-pink-950/30 border border-pink-500/30 rounded p-4 mb-4">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-lg font-bold text-pink-300">🎯 TARGETING_STRATEGY</h4>
              <button
                onClick={() => copyCampaignData(JSON.stringify(campaign.targeting, null, 2))}
                className="text-xs bg-pink-500/20 px-2 py-1 rounded hover:bg-pink-500/30"
              >
                [COPY_TARGETING]
              </button>
            </div>
            
            <div className="mb-3">
              <h5 className="text-sm font-bold text-pink-400 mb-2">DEMOGRAPHICS:</h5>
              <div className="bg-black/30 p-2 rounded text-sm">
                <div className="text-pink-300">AGE: {campaign.targeting?.demographics?.age_range?.join(", ")}</div>
                <div className="text-pink-300">GENDER: {campaign.targeting?.demographics?.gender?.join(", ")}</div>
                <div className="text-pink-300">LOCATIONS: {campaign.targeting?.demographics?.locations?.join(", ")}</div>
              </div>
            </div>

            <div className="mb-3">
              <h5 className="text-sm font-bold text-pink-400 mb-2">INTERESTS:</h5>
              <div className="flex flex-wrap gap-1">
                {campaign.targeting?.interests?.map((interest: string, index: number) => (
                  <span key={index} className="bg-pink-500/20 text-pink-300 px-2 py-1 rounded text-xs">
                    {interest}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <h5 className="text-sm font-bold text-pink-400 mb-2">BEHAVIORS:</h5>
              <div className="flex flex-wrap gap-1">
                {campaign.targeting?.behaviors?.map((behavior: string, index: number) => (
                  <span key={index} className="bg-pink-500/20 text-pink-300 px-2 py-1 rounded text-xs">
                    {behavior}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Ad Creatives */}
          <div className="bg-pink-950/30 border border-pink-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-pink-300 mb-3">🎨 AD_CREATIVES</h4>
            <div className="space-y-3">
              {campaign.ad_creatives?.map((ad: any, index: number) => (
                <div key={index} className="bg-black/30 p-3 rounded">
                  <div className="flex justify-between items-center mb-2">
                    <h5 className="text-sm font-bold text-pink-300">
                      {ad.ad_set_name} - {ad.format}
                    </h5>
                    <button
                      onClick={() => copyCampaignData(JSON.stringify(ad, null, 2))}
                      className="text-xs bg-pink-500/20 px-2 py-1 rounded hover:bg-pink-500/30"
                    >
                      [COPY_AD]
                    </button>
                  </div>
                  
                  <div className="mb-2">
                    <span className="text-pink-400 text-xs">HEADLINE:</span>
                    <div className="text-pink-300 text-sm font-bold">{ad.headline}</div>
                  </div>

                  <div className="mb-2">
                    <span className="text-pink-400 text-xs">PRIMARY_TEXT:</span>
                    <div className="text-pink-300 text-sm">{ad.primary_text}</div>
                  </div>

                  <div className="mb-2">
                    <span className="text-pink-400 text-xs">CTA:</span>
                    <div className="text-pink-300 text-sm bg-pink-600/20 p-1 rounded inline-block">
                      {ad.cta_button}
                    </div>
                  </div>

                  {ad.ab_test_variants && (
                    <div className="mt-2">
                      <span className="text-pink-400 text-xs">A/B_TEST:</span>
                      <div className="text-pink-300 text-xs mt-1">
                        A: {ad.ab_test_variants[0]?.headline}
                        <br />
                        B: {ad.ab_test_variants[1]?.headline}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Funnel Strategy */}
          <div className="bg-pink-950/30 border border-pink-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-pink-300 mb-3">🚀 FUNNEL_STRATEGY</h4>
            <div className="space-y-2">
              {Object.entries(campaign.funnel_strategy || {}).map(([stage, data]: [string, any], index: number) => (
                <div key={index} className="bg-black/30 p-2 rounded">
                  <div className="text-pink-300 font-bold">{stage.replace("_", " ").toUpperCase()}</div>
                  <div className="text-pink-300 text-sm">
                    <span className="text-pink-400">Objective:</span> {data.objective}
                    <span className="ml-3 text-pink-400">Budget:</span> {data.budget_percentage}%
                  </div>
                  <div className="text-pink-300 text-xs">
                    <span className="text-pink-400">Ad Types:</span> {data.ad_types?.join(", ")}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bidding Strategy */}
          <div className="bg-pink-950/30 border border-pink-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-pink-300 mb-3">💰 BIDDING_STRATEGY</h4>
            <div className="bg-black/30 p-2 rounded">
              <div className="text-pink-300">
                <span className="text-pink-400">Strategy:</span> {campaign.bidding_strategy?.strategy}
              </div>
              {campaign.bidding_strategy?.target_cpa && (
                <div className="text-pink-300">
                  <span className="text-pink-400">Target CPA:</span> R$ {campaign.bidding_strategy.target_cpa}
                </div>
              )}
              {campaign.bidding_strategy?.target_roas && (
                <div className="text-pink-300">
                  <span className="text-pink-400">Target ROAS:</span> {campaign.bidding_strategy.target_roas}
                </div>
              )}
            </div>
          </div>

          {/* Tracking & Analytics */}
          <div className="bg-pink-950/30 border border-pink-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-pink-300 mb-3">📈 TRACKING_ANALYTICS</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="bg-black/30 p-2 rounded">
                <span className="text-pink-400 text-xs">Facebook Pixel:</span>
                <div className="text-pink-300 text-xs font-mono">{campaign.tracking_and_pixels?.facebook_pixel}</div>
              </div>
              <div className="bg-black/30 p-2 rounded">
                <span className="text-pink-400 text-xs">Google Analytics:</span>
                <div className="text-pink-300 text-xs font-mono">{campaign.tracking_and_pixels?.google_analytics}</div>
              </div>
            </div>
            
            <div className="mt-2">
              <span className="text-pink-400 text-xs">Custom Events:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {campaign.tracking_and_pixels?.custom_events?.map((event: string, index: number) => (
                  <span key={index} className="bg-pink-500/20 text-pink-300 px-2 py-1 rounded text-xs">
                    {event}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Performance Estimates */}
          <div className="bg-pink-950/30 border border-pink-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-pink-300 mb-3">📊 ESTIMATED_PERFORMANCE</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              <div className="bg-black/30 p-2 rounded text-center">
                <div className="text-pink-300 font-bold">{campaign.estimated_performance?.expected_ctr}</div>
                <div className="text-pink-400 text-xs">CTR</div>
              </div>
              <div className="bg-black/30 p-2 rounded text-center">
                <div className="text-pink-300 font-bold">{campaign.estimated_performance?.expected_cpc}</div>
                <div className="text-pink-400 text-xs">CPC</div>
              </div>
              <div className="bg-black/30 p-2 rounded text-center">
                <div className="text-pink-300 font-bold">{campaign.estimated_performance?.expected_conversions}</div>
                <div className="text-pink-400 text-xs">Conversions</div>
              </div>
              <div className="bg-black/30 p-2 rounded text-center">
                <div className="text-pink-300 font-bold">{campaign.estimated_performance?.reach_estimate}</div>
                <div className="text-pink-400 text-xs">Reach</div>
              </div>
            </div>
          </div>

          {/* Campaign Timeline */}
          <div className="bg-pink-950/30 border border-pink-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-pink-300 mb-3">📅 CAMPAIGN_TIMELINE</h4>
            <div className="space-y-1">
              {campaign.campaign_timeline?.map((item: any, index: number) => (
                <div key={index} className="bg-black/30 p-2 rounded text-sm">
                  <span className="text-pink-400">Day {item.day}:</span>
                  <span className="text-pink-300 ml-2">{item.action}</span>
                  <span className="text-pink-400 ml-2">({item.budget_allocation})</span>
                </div>
              ))}
            </div>
          </div>

          <div className="text-xs text-pink-400 mt-4">
            [CAMPAIGN_COMPLETE] | SETUP_TIME: {campaign.metadata?.estimatedSetupTime} | COMPLEXITY: {campaign.metadata?.campaignComplexity} | AUTH: ADMIN
          </div>
        </div>
      )}
    </div>
  );
}
